/**
 * /internal/loom - the Loom panel workbench.
 *
 * The workbench itself (viewer shell, data packs, solver) is served by
 * thread-api at ``/api/loom-workbench``. This page is only the frame: it
 * reads the Thread session, hands the access token to that route as a query
 * parameter, and embeds the result full-viewport. The API sets its own
 * HttpOnly cookie from that token, so the viewer's subresources, data packs
 * and solve calls authenticate without a token on every URL.
 *
 * Same shape as ``DocsPage`` (which frames ``/api/internal-docs``). The
 * @flexcompute.com gate lives in two places on purpose: ``FlexcomputeOnlyGuard``
 * in ``App.tsx`` keeps non-Flex users out of the UI, and the API re-checks the
 * identity on every request.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import type { Session } from "../lib/auth";
import { authClient } from "../lib/auth";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

type WorkbenchState =
  | { stage: "loading" }
  | { stage: "ready"; url: string; email?: string }
  | { stage: "needs-thread-session" }
  | { stage: "error"; message: string };

/**
 * Authenticated entry URL for the workbench viewer shell.
 *
 * Exported so the token handling is unit-testable without rendering the page.
 */
export function buildLoomWorkbenchUrl(token: string): string {
  return `${API_BASE}/loom-workbench?token=${encodeURIComponent(token)}`;
}

function currentAppPath(location: ReturnType<typeof useLocation>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export default function LoomWorkbench() {
  const location = useLocation();
  const [state, setState] = useState<WorkbenchState>({ stage: "loading" });

  useEffect(() => {
    let cancelled = false;

    authClient.auth
      .getSession()
      .then(({ data }: { data: { session: Session | null } }) => {
        if (cancelled) return;
        const token = data.session?.access_token;
        if (!token) {
          setState({ stage: "needs-thread-session" });
          return;
        }
        setState({
          stage: "ready",
          url: buildLoomWorkbenchUrl(token),
          email: data.session?.user?.email,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ stage: "error", message: String(err) });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithGoogle = async () => {
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const redirectTo = `${window.location.origin}${base}/auth/callback?next=${encodeURIComponent(currentAppPath(location))}`;
    const { error } = await authClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) {
      setState({ stage: "error", message: error.message });
    }
  };

  if (state.stage === "ready") {
    return (
      <iframe
        src={state.url}
        title={
          state.email
            ? `Loom panel workbench for ${state.email}`
            : "Loom panel workbench"
        }
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          border: 0,
          background: "var(--fc-surface)",
        }}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "var(--fc-sp-24, 24px)",
        background: "var(--fc-paper)",
        color: "var(--fc-ink)",
        fontFamily: "var(--fc-font-sans)",
      }}
    >
      <div
        style={{
          width: 420,
          maxWidth: "100%",
          padding: "var(--fc-sp-32, 32px)",
          textAlign: "center",
          background: "var(--fc-surface)",
          border: "1px solid var(--fc-border)",
          borderRadius: "var(--fc-radius-lg, 10px)",
        }}
      >
        <h1
          style={{
            margin: 0,
            marginBottom: "var(--fc-sp-16, 16px)",
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: "-0.01em",
          }}
        >
          Loom panel workbench
        </h1>
        {state.stage === "loading" && (
          <p style={{ margin: 0, color: "var(--fc-ink-4)", lineHeight: 1.5 }}>
            Loading the workbench…
          </p>
        )}
        {state.stage === "needs-thread-session" && (
          <>
            <p
              style={{
                margin: 0,
                marginBottom: "var(--fc-sp-24, 24px)",
                color: "var(--fc-ink-4)",
                lineHeight: 1.5,
              }}
            >
              The panel workbench requires a Flexcompute Thread session.
            </p>
            <button
              type="button"
              onClick={signInWithGoogle}
              style={{
                fontFamily: "inherit",
                fontSize: 14,
                padding: "10px 20px",
                cursor: "pointer",
                color: "var(--fc-surface)",
                background: "var(--fc-primary)",
                border: "1px solid var(--fc-primary)",
                borderRadius: "var(--fc-radius-md, 6px)",
              }}
            >
              Continue with Google
            </button>
          </>
        )}
        {state.stage === "error" && (
          <p
            style={{
              margin: 0,
              color: "var(--fc-danger)",
              fontFamily: "var(--fc-font-mono)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {state.message}
          </p>
        )}
      </div>
    </div>
  );
}
